import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { REGEX_CODFIS, REGEX_EMAIL, REGEX_EIDAS } from 'app/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { faHeadphonesAlt, faRedoAlt, faVolumeUp, faSyncAlt, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { UserService as CUserService } from '../../services/user.service';
import { ChallengeAnswer, ChallengeQuestion, ForgotData, IForgotData } from 'app/core/user/updateObject.model';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { DataSharingService } from 'app/layouts/homepage/services/data-sharing.service';
import { CAPTCHA_ENABLED } from 'app/app.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-forgot-username',
    templateUrl: './forgot-username.component.html',
    styleUrls: ['../../../../../content/scss/styles.scss']
})
export class ForgotUsernameComponent implements OnInit {
    readonly REGEX_CODFIS = REGEX_CODFIS;
    readonly REGEX_EMAIL = REGEX_EMAIL;
    readonly REGEX_EIDAS = REGEX_EIDAS;
    usernameForm: FormGroup;
    submitted = false;
    appUrl = '';
    faHeadphones = faHeadphonesAlt;
    faRedo = faRedoAlt;
    faEyeSlash = faEyeSlash;
    faEye = faEye;
    faVolumeUp = faVolumeUp;
    faSyncAlt = faSyncAlt;
    successMsg: string;
    imageToShow: string | ArrayBuffer;
    private captchaId = '';
    captchaPassed = false;
    captchaEnabled = CAPTCHA_ENABLED;  // Read from build-time constant
    confirmData: ChallengeQuestion;
    showAnswer = false;
    errorMsg = '';
    channelEmail = false;
    channelEmailAlt = false;
    channelSMS = false;
    channelToBeUsed: string;
    defaultRadioValue: string;
    errorCheckStepOne = false;
    errorCheckStepTwo = false;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private cUserService: CUserService,
        private router: Router,
        private dataSharingService: DataSharingService,
        private translateService: TranslateService
    ) {
        this.createForm();
        this.appUrl = this.route.snapshot.queryParamMap.get('appUrl');
    }

    ngOnInit() {
        // Conditionally load captcha based on build-time constant
        console.log('=== CAPTCHA DEBUG ===');
        console.log('captchaEnabled:', this.captchaEnabled);
        console.log('CAPTCHA_ENABLED constant:', CAPTCHA_ENABLED);
        
        if (this.captchaEnabled) {
            console.log('Calling getCaptcha()...');
            this.getCaptcha();
        } else {
            console.log('Captcha DISABLED - clearing validators');
            // If captcha is disabled, remove validation and mark as passed
            this.usernameForm.get('captcha').clearValidators();
            this.usernameForm.get('captcha').updateValueAndValidity();
            this.captchaPassed = true;
        }
    }

    private createForm() {
        this.usernameForm = this.formBuilder.group({
            codFiscale: ['', [Validators.required, this.userIdentifierValidator.bind(this)]],
            captcha: ['', [Validators.required]],
            question: [''],
            answer: [{ disabled: true, value: '' }, [Validators.required]],
            invio: [{ value: '' }, [Validators.required]]
        });
    }

    /**
     * Validatore custom che supporta 3 formati:
     * - Codice Fiscale standard (utenti standard)
     * - Email (utenti EXTRA_UE)
     * - Formato eIDAS: ^(?=.{1,256}$)[A-Z]{2}/(?:[A-Z]{2}|EU)/(?:§+|[ A-Za-z0-9+/]+={0,2}|[A-Za-z0-9 ]+={0,2})$
     */
    private userIdentifierValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // required validator gestisce il campo vuoto
        }

        const value = control.value.trim();
        
        // Test CF standard
        const cfRegex = new RegExp(this.REGEX_CODFIS);
        if (cfRegex.test(value)) {
            return null; // valido
        }

        // Test Email (EXTRA_UE)
        const emailRegex = new RegExp(this.REGEX_EMAIL);
        if (emailRegex.test(value)) {
            return null; // valido
        }

        // Test eIDAS
        const eidasRegex = new RegExp(this.REGEX_EIDAS);
        if (eidasRegex.test(value)) {
            return null; // valido
        }

        return { pattern: true }; // nessun formato valido
    }

    // ngOnInit() {
    //     this.registerForm = this.formBuilder.group({
    //         title: ['', Validators.required],
    //         firstName: ['', Validators.required],
    //         lastName: ['', Validators.required],
    //         email: ['', [Validators.required, Validators.email]],
    //         password: ['', [Validators.required, Validators.minLength(6)]],
    //         confirmPassword: ['', Validators.required],
    //         acceptTerms: [false, Validators.requiredTrue]
    //     }, {
    //         validator: MustMatch('password', 'confirmPassword')
    //     });
    // }
    // convenience getter for easy access to form fields
    get f() {
        return this.usernameForm.controls;
    }
    get fCodiceFiscale() {
        return this.usernameForm.get('codFiscale');
    }
    get fCaptcha() {
        return this.usernameForm.get('captcha');
    }
    get fQuestion() {
        return this.usernameForm.get('question');
    }
    get fAnswer() {
        return this.usernameForm.get('answer');
    }
    get radioChoice() {
        return this.usernameForm.get('invio');
    }

    onSubmit(event: any) {
        if (!event) {
            // fix firefox double submit
            return;
        }

        if (this.confirmData && this.usernameForm.controls['answer'].value !== '') {
            if (this.usernameForm.controls['answer'].value === null) {
                this.errorCheckStepTwo = true;
            } else {
                this.sendRequest();
                return;
            }
        }
        if (this.confirmData && this.usernameForm.controls['answer'].value === '') {
            this.errorCheckStepTwo = true;
        }
        this.errorCheckStepOne = true;
        // stop here if form is invalid
        if (this.usernameForm.invalid) {
            return;
        }
        this.submitted = true;
        const outObj: IForgotData = new ForgotData(this.fCodiceFiscale.value, null, this.fCaptcha.value, this.captchaId);
        
        // CHECK PREVENTIVO: verifica se l'utente può procedere (passwordless + EXTRA_UE)
        console.log('=== PRE-CHECK (Username Recovery): Verifying if user can proceed ===');
        console.log('Codice Fiscale:', this.fCodiceFiscale.value);
        
        this.cUserService
            .checkPasswordless(outObj)
            .toPromise()
            .then(checkRes => {
                console.log('Pre-check response:', checkRes);
                if (checkRes && checkRes.body && checkRes.body.canProceed === false) {
                    // Utente non può procedere - mostra messaggio e non fare altre chiamate
                    console.log('User CANNOT proceed - blocking');
                    this.errorMsg = this.translateService.instant('error.functionality.not.available');
                    this.submitted = false;
                    return Promise.reject({ skipChallengeCall: true });
                }
                console.log('User CAN proceed - calling getChallengeQuestion');
                // Utente può procedere - chiama getChallengeQuestion
                return this.cUserService.proceedForgotCredential(outObj).toPromise();
            })
            .then(res => {
                if (res && res.body && res.body) {
                    this.confirmData = res.body;
                    this.patchForm();
                    this.fAnswer.enable();
                } else {
                    this.successMsg = 'Operazione effettuata con successo';
                }
            })
            .catch(err => {
                // Se è stato bloccato dal pre-check, non fare nulla (messaggio già impostato)
                if (err && err.skipChallengeCall) {
                    console.log('Skipped challenge call due to pre-check failure');
                    return;
                }
                
                if (err && err.error) {
                    this.errorMsg = err.error.message;
                    this.getCaptcha();
                }
            })
            .finally(() => {
                this.submitted = false;
            });
    }

    onReset() {
        if (!this.confirmData) {
            this.usernameForm.controls['codFiscale'].reset();
            this.usernameForm.controls['captcha'].reset();
            this.errorCheckStepOne = false;
        }
        if (this.confirmData) {
            this.usernameForm.controls['answer'].reset();
            this.errorCheckStepTwo = false;
        }
        // this.submitted = false;
    }

    backToApp() {
        this.router.navigate(['/']);
    }

    createImageFromBlob(image: Blob) {
        console.log('createImageFromBlob - image blob:', image);
        const reader = new FileReader();
        reader.addEventListener(
            'load',
            () => {
                console.log('Image loaded, data URL length:', reader.result ? reader.result.toString().length : 0);
                this.imageToShow = reader.result;
            },
            false
        );

        if (image) {
            reader.readAsDataURL(image);
        } else {
            console.error('No image blob provided');
        }
    }

    getCaptcha() {
        this.captchaId = uuidv4();
        console.log('getCaptcha() - captchaId:', this.captchaId);
        this.cUserService
            .requestCaptcha(this.captchaId)
            .toPromise()
            .then(res => {
                console.log('Captcha response received:', res);
                this.createImageFromBlob(res.body);
            })
            .catch(err => {
                console.error('Error loading captcha:', err);
            });
    }

    getCaptchaSound() {
        this.cUserService
            .requestCaptchaSound(this.captchaId)
            .toPromise()
            .then(res => {
                const audio = new Audio(res.url);
                audio.play();
            })
            .catch(err => {
                // console.log(err);
            });
    }

    changeShowAnswer() {
        this.showAnswer = !this.showAnswer;
    }

    sendRequest() {
        this.submitted = true;
        this.channelToBeUsed = this.radioChoice.value;
        this.cUserService
            .confirmForgotUsernameRequest(
                new ChallengeAnswer(this.fCodiceFiscale.value, null, this.fAnswer.value, this.channelToBeUsed, this.confirmData.tempUUID)
            )
            .toPromise()
            .then(res => {
                if (res && res.body && res.body.message) {
                    this.successMsg = res.body.message;
                } else {
                    this.successMsg = 'Operazione effettuata con successo';
                }
            })
            .catch(err => {
                this.verifyValidationError(err);
            })
            .finally(() => {
                this.submitted = false;
            });
    }

    verifyValidationError(err: HttpErrorResponse) {
        if (err && err.error) {
            if (err.error.message === 'error.validation') {
                this.errorMsg = 'Si è verificato un errore nella validazione dei campi.';
            } else {
                this.errorMsg = err.error.message;
            }
        }
    }

    private patchForm() {
        this.fCodiceFiscale.patchValue(this.confirmData.cf);
        this.fQuestion.patchValue(this.confirmData.challengeQuestion);
        this.channelEmail = this.confirmData.validEmail;
        this.channelEmailAlt = this.confirmData.validAltEmail;
        this.channelSMS = this.confirmData.validSMS;

        if (this.channelEmail === true) {
            this.defaultRadioValue = 'EMAIL';
        } else if (this.channelSMS === true) {
            this.defaultRadioValue = 'SMS';
        } else if (this.channelEmailAlt === true) {
            this.defaultRadioValue = 'ALT_EMAIL';
        } else {
            this.defaultRadioValue = null;
        }
        this.radioChoice.patchValue(this.defaultRadioValue);
    }

    get isDesktop(): boolean {
        return this.dataSharingService.isDesktop();
    }
}
